// "Customize" dialog for a person's own quick-access bar.
//
// Feedback #835 (James, 2026-09-30).  Opened from the ⚙️ control at the end of
// QuickAccessBar.  Saves to the person's preferences:
//   Save                    -> PUT /api/profile/preferences { quick_bar_items: [...] }
//   Reset to firm default   -> PUT /api/profile/preferences { quick_bar_items: null }
// and hands the server's answer to onSaved so the bar redraws at once — no
// reload.  The server is the judge of what is valid (lib/quickBarItems.js on
// the API); its 400 message is shown inline, never in a window.alert.
//
// Where the draft starts:
//   - the person has a bar of their own -> exactly what they stored, INCLUDING
//     a page they cannot open right now (a firm feature switched off for a
//     day, a permission taken away).  It is listed, flagged, so it can be
//     seen and removed; filtering it out here would delete it from their bar
//     on the next unrelated Save, without a word, and it would not come back
//     when the feature did.  The firm-default editor in Settings already
//     works this way.
//   - they are on the firm or built-in default -> only the pages they can
//     open.  Copying an unopenable page out of someone else's default into
//     their own bar would store a page they never saw.

import { useEffect, useState } from 'react'
import api from '../services/api'
import { useModal } from '../hooks/useModal'
import QuickBarEditor from './QuickBarEditor'
import {
  QUICK_BAR_BUILTIN_DEFAULT, effectiveQuickBarKeys, visibleQuickBarItems,
  normalizeQuickBarKeys, describeQuickBarKeys,
} from '../lib/quickBarItems'

const sameKeys = (a, b) => a.length === b.length && a.every((k, i) => k === b[i])

export default function QuickBarCustomizeModal ({
  userItems, firmDefault, user, isFeatureOn, onClose, onSaved,
}) {
  const initial = () => normalizeQuickBarKeys(userItems)
    ?? visibleQuickBarItems(effectiveQuickBarKeys(userItems, firmDefault), user, isFeatureOn).map((i) => i.key)
  const [draft, setDraft] = useState(initial)
  const [startedFrom] = useState(initial)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const usingDefault = normalizeQuickBarKeys(userItems) === null
  const firmKeys = normalizeQuickBarKeys(firmDefault)
  const defaultName = firmKeys === null ? 'the standard bar' : "your firm's default"
  const defaultKeys = firmKeys === null ? [...QUICK_BAR_BUILTIN_DEFAULT] : firmKeys
  const dirty = !sameKeys(draft, startedFrom)

  const close = () => { if (!saving) onClose() }
  const { containerProps } = useModal({ onClose: close, escToClose: !saving })

  // Esc closes this dialog and nothing else.  useModal listens on `document`
  // but does not stop the event, so on the Calendar page in full screen the
  // same Esc would also reach Calendar's window-level "Esc exits full screen".
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') e.stopPropagation() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const persist = async (quickBarItems) => {
    setSaving(true)
    setError('')
    try {
      const res = await api.put('/api/profile/preferences', { quick_bar_items: quickBarItems })
      const data = res?.data || {}
      onSaved({
        items: 'quick_bar_items' in data ? data.quick_bar_items : quickBarItems,
        firmDefault: 'quick_bar_firm_default' in data ? data.quick_bar_firm_default : firmDefault,
      })
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save your quick bar just now.  Nothing was changed — please try again.')
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4"
      onClick={() => { if (!dirty) close() }}>
      <div {...containerProps} aria-labelledby="quick-bar-customize-title"
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col outline-none"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-gray-100">
          <div>
            <h3 id="quick-bar-customize-title" className="text-lg font-semibold text-gray-800">Customize your quick bar</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {usingDefault
                ? `You're using ${defaultName}.  Changes here apply only to you.`
                : 'This is your own bar.  Changes here apply only to you.'}
            </p>
          </div>
          <button type="button" onClick={close} disabled={saving} aria-label="Close"
            className="text-gray-400 hover:text-gray-700 text-xl leading-none px-1">×</button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          <QuickBarEditor value={draft} onChange={(next) => { setDraft(next); setError('') }}
            user={user} isFeatureOn={isFeatureOn} disabled={saving}
            unavailableNote="Not available to you right now — hidden from your bar; it comes back if it becomes available again" />
        </div>

        {error && (
          <div role="alert" className="mx-5 mb-2 bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2 text-sm">{error}</div>
        )}

        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-t border-gray-100">
          <button type="button" disabled={saving || usingDefault}
            onClick={() => persist(null)}
            title={usingDefault
              ? `You're already using ${defaultName}`
              : `Go back to ${defaultName}: ${describeQuickBarKeys(defaultKeys)}`}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            Reset to firm default
          </button>
          <div className="flex-1" />
          <button type="button" onClick={close} disabled={saving}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={() => persist(draft)} disabled={saving || !dirty}
            className="px-4 py-1.5 text-sm rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
